import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Container, Paper, Divider, Slider, Dialog, Slide } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IContest, ISaleObject } from '../../types/global/helper';
import { PaymentIntent, PaymentRequestPaymentMethodEvent, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { IPaymentMethodSelectorRef, IPType, PaymentMethodSelector } from '../Checkout/PaymentMethodSelector';
import { getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { showToastMessage } from '../../utils/AppUtils';
import { ICheckoutItem, ICurrencyInfo } from '../../redux/checkout/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { ICampaignSales, IContestAwardCategories } from '../../redux/contest/types';
import { TransitionProps } from '@mui/material/transitions';
import { IPaymentCard } from '../../types/global/generic.types';
import { processPayment } from '../../redux/checkout/action';
import { useStripePromise } from '../../hook/useStripePromise';
import { campaignPricingSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';

interface CampaignFundingModalProps {
    open: boolean;
    onClose: () => void;
    contest: IContest;
    onFundComplete: (paymentIntent: ICampaignSales) => void;
    awards: IContestAwardCategories;
    saleObject?: ISaleObject;
    currencyInfo?: ICurrencyInfo;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/** Company Fee Calculations */
let CAMPAIGN_MAX = 20000;
let PERCENTAGE = 0.15;
let FLAT_FEE = 50;

const CampaignFundingModal: React.FC<CampaignFundingModalProps> = ({
    open,
    onClose,
    contest,
    onFundComplete,
    awards,
    currencyInfo,
    saleObject,
}) => {
    const { themeStyle, device } = useThemeContext();
    const campaignPricing = useSelector(campaignPricingSelector);
    const [isValidCard, setIsValidCard] = useState(false);
    const [paymentType, setPaymentType] = useState<IPType>();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<IPaymentCard | null>(null);
    const [processing, setProcessing] = useState(false);
    const [totalAmount, setTotalAmount] = useState(1);
    const currentSaleObject = saleObject;
    const paymentRef = useRef<IPaymentMethodSelectorRef | null>(null);
    const dispatchPromise = useDispatchPromise();
    const stripePromise = useStripePromise();
    const { user } = getJWtDetails();

    if (campaignPricing.campaignFlatFee) {
        FLAT_FEE = campaignPricing.campaignFlatFee / 100;
    }
    if (campaignPricing.campaignMax) {
        CAMPAIGN_MAX = campaignPricing.campaignMax / 100;
    }
    if (campaignPricing.campaignPercentage) {
        PERCENTAGE = campaignPricing.campaignPercentage / 100;
    }

    // Calculate minimum budget based on awards and prizes
    const calculateMinBudget = useCallback(() => {
        if (!awards.categories) return 0;
        let total = 0;
        Object.entries(awards.categories).forEach(([_, award]) => {
            if (award.prize) {
                total += Number(award.prize);
            }
        });
        if (contest && contest.amount_available && contest.amount_available >= total) {
            return 100;
        }

        return total < 100 ? 100 : total;
    }, [awards.categories, contest]);

    // Calculate fees
    const calculateFees = (budget: number) => {
        const flatFee = FLAT_FEE;
        const percentageFee = budget * PERCENTAGE;
        return {
            flatFee,
            percentageFee,
            total: budget + flatFee + percentageFee,
            minAmount: budget,
            actual_amount: budget,
        };
    };

    // Initialize total amount with minimum budget
    useEffect(() => {
        const minBudget = calculateMinBudget();
        const fees = calculateFees(minBudget);

        setTotalAmount(fees.minAmount);
    }, [contest, calculateMinBudget]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        console.log(
            'CampaignFundingModal: Slider changed to:',
            newValue,
            'new total with fees:',
            calculateFees(newValue as number).total,
        );
        setTotalAmount(newValue as number);
    };

    const handleCompletePurchase = async (paymentIntent: PaymentIntent) => {
        const saleObject: ISaleObject = {
            ...currentSaleObject,
        } as ISaleObject;
        if (currentSaleObject) {
            const payload: ICheckoutItem = {
                id: contest?._id!,
                title: contest?.title!,
                price: fees.total,
                original_price: fees.total,
                currency: 'usd',
                quantity: 1,
                breakdown: fees,
            };
            try {
                dispatchPromise(
                    processPayment({
                        items: [payload],
                        total: fees.total,
                        paymentMethod: selectedPaymentMethod as IPaymentCard,
                        currency: 'usd',
                        category: 'funding',
                        metadata: {
                            email: user?.email,
                            name: `${user?.firstName} ${user?.lastName}`,
                            currencyInfo: currencyInfo,
                            paymentIntent,
                        },
                        saleObject,
                    }),
                ).then((sale: any) => {
                    if (sale) {
                        onFundComplete(sale);
                    }
                });
            } catch (error) {
                console.error('Payment processing error:', error);
            }
        }
    };

    const handleSubmit = async () => {
        if (!contest) return;

        setProcessing(true);
        try {
            const fees = calculateFees(totalAmount);
            const payload: ICheckoutItem = {
                id: contest._id,
                title: contest.title,
                price: fees.total ?? 1,
                original_price: fees.total,
                currency: 'usd',
                quantity: 1,
                breakdown: fees,
            };

            await paymentRef.current?.handleSubmit(
                currentSaleObject?.transaction_currency!,
                Math.round(fees.total * 100),
                {
                    email: user?.email || '',
                    name: user ? `${user.firstName} ${user.lastName}` : '',
                    saveCard: false,
                    address: {
                        line1: '',
                        line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'US',
                    },
                },
                {
                    items: [payload],
                    contestId: contest._id,
                    category: 'funding',
                    currency: currentSaleObject?.transaction_currency,
                    metadata: {
                        linked_event: contest._id,
                        linked_event_type: 'Contest',
                        useAddressOnFile: true,
                        currencyInfo: currencyInfo,
                        name: `${user?.firstName} ${user?.lastName}`,
                        email: user?.email,
                        contest: contest._id,
                    },
                },
                async paymentIntent => {
                    if (paymentIntent.status === 'succeeded') {
                        handleCompletePurchase(paymentIntent);
                    } else {
                        showToastMessage('Payment failed. Please try again.', 'error');
                    }
                },
            );
        } catch (error) {
            showToastMessage('Payment processing failed. Please try again.', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const disableButton = () => {
        if (paymentType === 'new_card') {
            return !isValidCard;
        }
        if (paymentType === 'saved_card') {
            return !selectedPaymentMethod;
        }
        return !paymentType;
    };

    // Stripe Elements options
    const options: StripeElementsOptions = useMemo(() => {
        return {
            mode: 'payment',
            amount: totalAmount < 100 ? 100 : Math.round(totalAmount * 100),
            currency: currencyInfo?.transaction_currency ?? 'usd',
            setup_future_usage: 'off_session',
            appearance: {
                theme: themeStyle === 'dark' ? 'night' : 'flat',
            },
        };
    }, [totalAmount, currencyInfo?.transaction_currency, themeStyle]);

    const fees = calculateFees(totalAmount);

    const renderPaymentSelector = useMemo(() => {
        return (
            <Elements stripe={stripePromise} options={options}>
                <PaymentMethodSelector
                    key={`payment-${Math.round(fees.total * 100)}`}
                    ref={paymentRef}
                    amount={Math.round(fees.total * 100)}
                    onPaymentMethodSelected={type => {
                        setPaymentType(type);
                    }}
                    onSelectSavedCard={card => {
                        setSelectedPaymentMethod(card);
                    }}
                    preselectCard
                    onChange={change => {
                        if (change.complete) {
                            setIsValidCard(true);
                        } else {
                            setIsValidCard(false);
                        }
                    }}
                    callBack={async (paymentIntent, stripeEvent?: PaymentRequestPaymentMethodEvent) => {
                        if (paymentIntent.status === 'succeeded') {
                            await handleCompletePurchase(paymentIntent);
                        } else {
                            showToastMessage('Payment failed. Please try again.', 'error');
                        }
                    }}
                    currency={currentSaleObject?.transaction_currency!}
                    details={{
                        email: user?.email || '',
                        name: user ? `${user.firstName} ${user.lastName}` : '',
                        saveCard: false,
                        address: {
                            line1: '',
                            line2: '',
                            city: '',
                            state: '',
                            postal_code: '',
                            country: 'US',
                        },
                    }}
                    metadata={{
                        items: [
                            {
                                id: contest._id,
                                title: contest.title,
                                price: fees.total ?? 1,
                                original_price: fees.total,
                                currency: 'usd',
                                quantity: 1,
                                breakdown: fees,
                            },
                        ],
                        contestId: contest._id,
                        category: 'funding',
                        currency: currentSaleObject?.transaction_currency,
                        metadata: {
                            linked_event: contest._id,
                            linked_event_type: 'Contest',
                            useAddressOnFile: true,
                            currencyInfo: currencyInfo,
                            name: `${user?.firstName} ${user?.lastName}`,
                            email: user?.email,
                            contest: contest._id,
                        },
                    }}
                />
            </Elements>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fees.total, fees, contest, currencyInfo, currentSaleObject, options, user, stripePromise, totalAmount]);

    return (
        <Dialog
            scroll="paper"
            open={open}
            onClose={() => onClose && onClose()}
            TransitionComponent={Transition}
            fullWidth={true}
            maxWidth={'lg'}
            style={{}}
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].black,
                    width: device.isMobile ? '100vw' : '90vw',
                    maxWidth: device.isMobile ? '100%' : '600px',
                    borderRadius: device.isMobile ? 0 : 10,
                    padding: '10px',
                    margin: '0px',
                    marginTop: device.isMobile ? '0' : '30%',
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    height: '100%',
                    maxHeight: device.isMobile ? '100%' : 'auto',
                },
            }}
            sx={{
                display: 'flex',
                height: { xs: '100vh', sm: '90%', lg: '90%' },
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
            }}>
            <Container
                sx={{
                    position: 'relative',
                    height: '100%',
                    alignItems: 'center',
                }}>
                <Box
                    sx={{
                        height: 'fit-content',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        gap: device.isMobile ? 2 : 5,
                    }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: device.isMobile ? '100%' : 'fit-content',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            sx={{
                                textAlign: 'left',
                                fontSize: 24,
                                fontStyle: 'normal',
                                fontWeight: 600,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            Fund Campaign
                        </Typography>
                        <CloseIcon
                            onClick={onClose}
                            style={{ cursor: 'pointer' }}
                            stroke={theme[themeStyle].tag_white}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 3,
                            width: '100%',
                        }}>
                        {/* Campaign Image */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 1,
                                width: '100%',
                            }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    backgroundColor: theme[themeStyle].gray_text,
                                    position: 'relative',
                                    height: '108px',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'clip',
                                }}>
                                {contest && (
                                    <CustomLazyLoadImage
                                        src={contest.feature_image}
                                        alt="Feature"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                        effect="blur"
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Fee Breakdown */}
                        <Paper
                            sx={{
                                p: 2,
                                mb: 2,
                                backgroundColor: theme[themeStyle].card_grey,
                                borderRadius: '8px',
                                width: '100%',
                            }}>
                            {/* Award Breakdown */}
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                    mb: 2,
                                }}>
                                Award Breakdown
                            </Typography>
                            {Object.entries(awards.categories).map(([name, award]) => (
                                <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {award.category_title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        ${Number(award.prize).toFixed(2)}
                                    </Typography>
                                </Box>
                            ))}
                            <Divider sx={{ my: 2, borderColor: theme[themeStyle].darkGray60 }} />

                            {/* Campaign Budget */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Campaign Budget
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    ${totalAmount.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Flat Fee
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    ${fees.flatFee.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Platform Fee ({PERCENTAGE * 100}%)
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    ${fees.percentageFee.toFixed(2)}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1, borderColor: theme[themeStyle].darkGray60 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Total
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    ${fees.total.toFixed(2)}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Budget Slider */}
                        <Box sx={{ width: '100%', px: 2 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].primary_white,
                                    mb: 2,
                                }}>
                                Adjust Budget
                            </Typography>
                            <Slider
                                value={totalAmount}
                                onChange={handleSliderChange}
                                min={calculateFees(calculateMinBudget()).minAmount}
                                max={CAMPAIGN_MAX}
                                step={50}
                                sx={{
                                    color: theme[themeStyle].primary_orange,
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                    },
                                    '& .MuiSlider-track': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: theme[themeStyle].darkGray60,
                                    },
                                }}
                            />
                        </Box>

                        {/* Payment Method */}
                        <Box sx={{ mb: 4, width: '100%' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                    mb: 2,
                                }}>
                                Payment Method
                            </Typography>
                            {renderPaymentSelector}
                        </Box>
                    </Box>
                </Box>

                {/* Submit Button */}
                <Box
                    sx={{
                        width: '100%',
                        height: 'fit-content',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        bottom: device.isMobile ? (window.outerHeight - window.innerHeight) * 0.3 : 0,
                        left: 0,
                        padding: device.isMobile ? 3 : 0,
                    }}>
                    <Button
                        onClick={handleSubmit}
                        sx={{
                            height: '52px',
                            borderRadius: 100,
                            fontSize: 16,
                            fontWeight: 600,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: '20px',
                            marginBottom: '20px',
                            textDecoration: 'none',
                            backgroundColor: theme[themeStyle].primary_orange,
                            color: theme[themeStyle].pure_black,
                            textTransform: 'none',
                            width: '100%',
                            '&:hover': {
                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                backgroundColor: theme[themeStyle].pure_black,
                                color: theme[themeStyle].primary_orange,
                            },
                            ':disabled': {
                                backgroundColor: theme[themeStyle].darkGray60,
                                color: theme[themeStyle].another_gray_color,
                                border: 'none',
                            },
                        }}
                        disabled={!!disableButton() || processing}>
                        {processing ? 'Processing...' : `Pay $${fees.total.toFixed(2)}`}
                    </Button>
                </Box>
            </Container>
        </Dialog>
    );
};

export { CampaignFundingModal };
